import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Phone, MapPin, Send, Loader2, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export const ContactPage = () => {
  const { t, settings, getLocalizedText, language } = useApp();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  // Romanian translations
  const translations = {
    ro: {
      pageTitle: 'Contact',
      getInTouch: 'Contactează-ne',
      haveQuestion: 'Ai întrebări despre produsele noastre? Ne-ar plăcea să auzim de la tine. Trimite-ne un mesaj și îți vom răspunde cât mai curând posibil.',
      companyInfo: 'Date Firmă',
      name: 'Nume',
      email: 'Email',
      phone: 'Telefon',
      subject: 'Subiect',
      message: 'Mesaj',
      sendMessage: 'Trimite Mesajul',
      messageSent: 'Mesajul a fost trimis cu succes!',
      address: 'Adresă',
      namePlaceholder: 'Numele tău complet',
      emailPlaceholder: 'adresa@email.com',
      phonePlaceholder: '07xx xxx xxx',
      subjectPlaceholder: 'Subiectul mesajului',
      messagePlaceholder: 'Scrie mesajul tău aici...',
    },
    en: {
      pageTitle: 'Contact',
      getInTouch: 'Get in Touch',
      haveQuestion: 'Have questions about our products? We\'d love to hear from you. Send us a message and we\'ll respond as soon as possible.',
      companyInfo: 'Company Info',
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      subject: 'Subject',
      message: 'Message',
      sendMessage: 'Send Message',
      messageSent: 'Message sent successfully!',
      address: 'Address',
      namePlaceholder: 'Your full name',
      emailPlaceholder: 'email@address.com',
      phonePlaceholder: '07xx xxx xxx',
      subjectPlaceholder: 'Message subject',
      messagePlaceholder: 'Write your message here...',
    }
  };

  const txt = translations[language] || translations.ro;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Simulate form submission
    await new Promise((r) => setTimeout(r, 1000));
    toast.success(txt.messageSent);
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    setLoading(false);
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8" data-testid="contact-page">
        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-sm tracking-[0.3em] uppercase text-gold mb-4">Rani Store</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#1A1625]">
            {txt.pageTitle}
          </h1>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-heading text-2xl font-semibold mb-6">{txt.getInTouch}</h2>
            <p className="text-muted-foreground mb-8">
              {txt.haveQuestion}
            </p>

            <div className="space-y-6">
              {/* Company Info Section */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-medium mb-2">{txt.companyInfo}</h3>
                  <div className="text-muted-foreground space-y-1 text-sm">
                    <p className="font-semibold text-foreground">S.C. RANISTORE S.R.L.</p>
                    <p>Str. Spiești, Nr. 51, Sector 3, București</p>
                    <p>J40 / 2443 / 2020</p>
                    <p>CUI 42291550</p>
                    <p>
                      <a href="tel:0732901128" className="hover:text-primary transition-colors">0732901128</a>
                    </p>
                    <p>
                      <a href="mailto:office@ranistore.ro" className="hover:text-primary transition-colors">office@ranistore.ro</a>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6 bg-surface p-8 rounded-lg">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">{txt.name} *</Label>
                  <Input
                    id="name"
                    required
                    placeholder={txt.namePlaceholder}
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="contact-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email">{txt.email} *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    placeholder={txt.emailPlaceholder}
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="contact-email"
                  />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="phone">{txt.phone} *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    placeholder={txt.phonePlaceholder}
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="contact-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="subject">{txt.subject} *</Label>
                  <Input
                    id="subject"
                    required
                    placeholder={txt.subjectPlaceholder}
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    data-testid="contact-subject"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="message">{txt.message} *</Label>
                <textarea
                  id="message"
                  required
                  rows={5}
                  placeholder={txt.messagePlaceholder}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                  data-testid="contact-message"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-primary hover:bg-purple-600 text-white rounded-full"
                data-testid="contact-submit"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Send className="mr-2 h-4 w-4" /> {txt.sendMessage}
                  </>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </Layout>
  );
};

export default ContactPage;
